<template>
  <main class="overflow-hidden pb-10 md:pt-10">
    <UiFixedHeader always-visible>
      <div class="flex items-center justify-between">
        <span class="line-clamp-1">{{ category?.name }}</span>
        <UiButton
          v-if="smallHeader"
          type="button"
          severity="quaternary"
          class="relative pr-4"
          @click="toggleHideHeader(false)"
        >
          <UiIcon name="three-dots" class="size-6 text-text-main" />
          <div
            v-if="selectedFiltersCount"
            class="absolute -right-0.5 -top-0.5 z-20 flex size-4 items-center justify-center rounded-full text-xxs text-light bg-main"
          >
            {{ selectedFiltersCount }}
          </div>
        </UiButton>
      </div>
    </UiFixedHeader>
    <div
      :class="smallHeader ? 'z-20' : 'z-30'"
      class="sticky top-13 bg-primary pl-4 md:top-0 md:pl-0 lg:pb-4"
    >
      <UiContainer class="mb-3 hidden items-center md:flex">
        <UiTitle severity="h1" class="!text-base md:!text-3xl">{{ category?.name }}</UiTitle>
      </UiContainer>
      <FilterSearch
        :id="SearchFilterIds.categoryViewSearchFilter"
        :page-size="PAGE_SIZE"
        hide-category
        :show-modal="isMdAndDown"
        class="pb-3 transition-all"
        :class="{ '!absolute -translate-y-full opacity-0': smallHeader }"
        @change="onFiltersChange"
      />
    </div>
    <UiContainer>
      <section v-if="!categoryLoadError" class="relative flex w-full flex-col">
        <CategoryResultsTiles :value="category" :loading="!categoryInitialized" />
        <UiEternalLoading
          v-if="!allEventsLoaded"
          :handler="loadMoreEvents"
          :all-loaded="allEventsLoaded"
        />
        <UiScrollUpButton class="!sticky bottom-20 z-20 ml-auto mr-0 md:bottom-10" />
      </section>
      <UiSearchError
        v-else
        class="flex flex-col items-center py-5"
        @reload="loadCategory(Number(route.params.id))"
      />
    </UiContainer>
  </main>
</template>

<script setup lang="ts">
import throttle from 'lodash/throttle'
import { storeToRefs } from 'pinia'
import { onBeforeMount } from 'vue'
import { useRoute, onBeforeRouteUpdate } from 'vue-router'
import { CategoryResultsTiles } from '@/3_widgets/Category'
import { FilterSearch } from '@/4_features/Search'
import { type CategoryFilters, useCategory } from '@/5_entities/Category'
import { useReloadOnConfigurationUpdate } from '@/5_entities/Configuration'
import { SearchFilterIds } from '@/5_entities/Search'
import { useFiltersCount, useHiddenHeader } from '@/6_shared/composables'
import { useBreakpoint } from '@/6_shared/lib'
import {
  UiButton,
  UiContainer,
  UiEternalLoading,
  UiFixedHeader,
  UiIcon,
  UiScrollUpButton,
  UiSearchError,
  UiTitle
} from '@/6_shared/ui'

const route = useRoute()
const PAGE_SIZE = 24
let filters: CategoryFilters = {
  pagination: { pageSize: PAGE_SIZE, currentPage: 1 },
  filter: {}
}
const { selectedFiltersCount, setFiltersCount } = useFiltersCount()
const onFiltersChange = (newFilters: CategoryFilters) => {
  setFiltersCount(newFilters)
  filters = newFilters
  loadCategory(Number(route.params.id))
}

const { category, categoryInitialized, allEventsLoaded, categoryLoadError } =
  storeToRefs(useCategory())
const { getCategory, loadNextEventsPage } = useCategory()

const loadCategory = (id: number) => getCategory(id, filters)
const loadMoreEvents = () => loadNextEventsPage(filters)

const {
  md: [isMdAndUp, isMdAndDown]
} = useBreakpoint()
const scrollHandler = throttle(function () {
  if (isMdAndUp.value) {
    return toggleHideHeader(false)
  }

  toggleHideHeader(window.scrollY >= minScroll)
})
const { smallHeader, toggleHideHeader, minScroll } = useHiddenHeader(scrollHandler)

useReloadOnConfigurationUpdate(() => loadCategory(Number(route.params.id)))
onBeforeMount(() => loadCategory(Number(route.params.id)))

onBeforeRouteUpdate(({ params: { id: toId } }, { params: { id: fromId } }) => {
  if (fromId !== toId) {
    loadCategory(Number(toId as string))
  }
})
</script>
